/**
 * Always-visible checklist for an onboarding run — one line per step,
 * grouped the way the build plan groups them (Integrations / Attribution /
 * Domain / Verify). Clicking a line scrolls to that step's card.
 */
import {
  CheckCircle2,
  Circle,
  CircleAlert,
  CircleDashed,
  Clock,
  XCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { OnboardingTask } from '@limo/database'
import {
  ONBOARDING_STEPS,
  type OnboardingStepGroup,
  type OnboardingStepStatus,
} from '@/lib/onboarding/steps'

const STATUS_ICONS: Record<
  OnboardingStepStatus,
  { icon: typeof Circle; className: string }
> = {
  pending: { icon: Circle, className: 'text-muted-foreground/50' },
  in_progress: { icon: CircleDashed, className: 'text-blue-600' },
  waiting_on_client: { icon: Clock, className: 'text-amber-600' },
  waiting_on_vendor: { icon: Clock, className: 'text-amber-600' },
  needs_review: { icon: CircleAlert, className: 'text-orange-600' },
  done: { icon: CheckCircle2, className: 'text-green-600' },
  error: { icon: XCircle, className: 'text-red-600' },
  skipped: { icon: Circle, className: 'text-muted-foreground/30' },
}

// Keep in sync with OnboardingRunPage: Domain leads so DNS goes out first.
const GROUP_ORDER: OnboardingStepGroup[] = [
  'Domain',
  'Attribution',
  'Integrations',
  'Verify',
]

export function OnboardingChecklist({ tasks }: { tasks: OnboardingTask[] }) {
  const taskByStep = new Map(tasks.map((task) => [task.step, task]))
  const doneCount = tasks.filter((t) => t.status === 'done').length
  // Denominator is the full registry, not just seeded rows — a run created
  // before a step was added still counts that step toward the total.
  const totalCount = ONBOARDING_STEPS.length

  const scrollToStep = (step: string) => {
    document
      .getElementById(`step-${step}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Checklist
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            {doneCount}/{totalCount} done
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {GROUP_ORDER.map((group) => {
          const steps = ONBOARDING_STEPS.filter((s) => s.group === group)
          if (steps.length === 0) return null
          return (
            <div key={group}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group}
              </p>
              <ul className="space-y-0.5">
                {steps.map((step) => {
                  const task = taskByStep.get(step.key)
                  const status = (task?.status ??
                    'pending') as OnboardingStepStatus
                  const { icon: Icon, className } = STATUS_ICONS[status]
                  return (
                    <li key={step.key}>
                      <button
                        type="button"
                        onClick={() => scrollToStep(step.key)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm',
                          'hover:bg-muted',
                          status === 'skipped' && 'opacity-50'
                        )}
                      >
                        <Icon className={cn('h-4 w-4 shrink-0', className)} />
                        <span className="truncate">{step.label}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
